import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import styles from "../styles/buy.module.css";
import CustomModal from "../components/CustomModal";
import { useLocation, useNavigate, Link } from "react-router-dom";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const pricingPlans = [
  {
    name: "Starter Pack",
    price: 500,
    credits: 100,
    features: [
      "**100 Application Credits**",
      "LinkedIn **Easy Apply** Automation",
      "Local & Secure Operation",
      "Standard Email Support",
      "Get Upto 3 interviews*.",
    ],
    baseApplications: 100,
    customizedApplications: 20,
  },
  {
    name: "Pro Pack",
    price: 1500,
    credits: 375,
    features: [
      "**375 Application Credits**",
      "LinkedIn **Easy Apply** Automation",
      "Local & Secure Operation",
      "Priority Email Support",
      "Get Upto 10 interviews*.",
    ],
    popular: true,
    baseApplications: 375,
    customizedApplications: 75,
  },
  {
    name: "Premium Pack",
    price: 3000,
    credits: 1000,
    features: [
      "**1000 Application Credits**",
      "LinkedIn **Easy Apply** Automation",
      "Local & Secure Operation",
      "Premium Chat Support",
      "Get Upto 25 interviews*.",
    ],
    baseApplications: 1000,
    customizedApplications: 200,
  },
];

const Buy = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    try {
      const userDataRaw = localStorage.getItem("userData");
      if (userDataRaw) {
        const userData = JSON.parse(userDataRaw);
        setUserEmail(userData?.email || null);
      }
    } catch (error) {
      console.error("Error reading user data:", error);
      setUserEmail(null);
    }

    const selectedPlanName =
      location.state?.selectedPlanName || location.state?.planToBuy;

    if (selectedPlanName) {
      const plan = pricingPlans.find((p) => p.name === selectedPlanName);
      if (plan) {
        setSelectedPlan(plan);
        if (JSON.parse(localStorage.getItem("userData"))?.email) {
          setShowEmailModal(true);
        }
      } else {
        navigate("/pricing");
      }
    } else {
      navigate("/pricing");
    }
  }, [location.state, navigate]);

  const handleStartPayment = async () => {
    if (!selectedPlan || !userEmail) {
      alert("Please select a plan and ensure you are logged in.");
      return;
    }
    setIsLoading(true);

    try {
      const orderRes = await fetch(`${BACKEND_URL}/api/payment/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          planName: selectedPlan.name,
          userEmail: userEmail,
        }),
      });

      const orderData = await orderRes.json();
      if (!orderRes.ok || !orderData.success) {
        throw new Error(orderData.message || "Order creation failed.");
      }
      const { order_id, amount } = orderData;

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: amount * 100,
        currency: "INR",
        name: "Job Assistant",
        description: `Purchase: ${selectedPlan.name}`,
        order_id: order_id,
        prefill: { email: userEmail },
        handler: async (response) => {
          setIsLoading(true);
          try {
            const paymentRes = await fetch(
              `${BACKEND_URL}/api/payment/process-payment`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  userEmail: userEmail,
                }),
              }
            );

            const paymentResponse = await paymentRes.json();
            if (paymentResponse.success) {
              alert("Payment successful! Credits added to your account.");
              navigate("/dashboard");
            } else {
              throw new Error(
                paymentResponse.message || "Payment verification failed."
              );
            }
          } catch (verificationError) {
            console.error("Payment verification error:", verificationError);
            alert(`Payment verification failed: ${verificationError.message}`);
          } finally {
            setIsLoading(false);
          }
        },
        modal: {
          ondismiss: () => {
            console.log("Payment modal dismissed");
            setShowCancelModal(true);
            setIsLoading(false);
          },
        },
        theme: { color: "#0a66c2" },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message;
      console.error("Payment error:", errorMessage);
      alert(`Payment failed: ${errorMessage}`);
      setIsLoading(false);
    }
  };

  const renderFeature = (featureText) => {
    const parts = featureText.split("**");
    return parts.map((part, index) =>
      index % 2 === 1 ? <strong key={index}>{part}</strong> : part
    );
  };

  if (!selectedPlan) {
    return (
      <div className={styles.container}>
        <p>Loading plan details...</p>
      </div>
    );
  }

  return (
    <>
      {showEmailModal && user?.email && (
        <CustomModal
          message={
            <>
              Purchase will be linked to <strong>{userEmail}</strong>. Proceed
              with payment?
            </>
          }
          onConfirm={() => setShowEmailModal(false)}
          onCancel={() => {
            setShowEmailModal(false);
            navigate("/pricing");
          }}
        />
      )}
      {showCancelModal && (
        <CustomModal
          message={
            <>
              Payment was <strong>cancelled</strong>. No charge was made.
            </>
          }
          onConfirm={() => setShowCancelModal(false)}
        />
      )}

      <div
        className={`${styles.container} ${
          showEmailModal || showCancelModal || isLoading ? styles.blurred : ""
        }`}
      >
        <Link to="/" className={styles.homeButton} title="Back to Home">
          <i className="fas fa-home" aria-hidden="true"></i>
        </Link>

        <div className={styles.header}>
          <h1>Confirm Your Purchase</h1>
          <p>You're about to upgrade your job search capabilities!</p>
        </div>

        <div className={styles.pricingCards}>
          <div className={`${styles.pricingCard}`}>
            {selectedPlan.popular && (
              <div className={styles.popularBadge}>BEST VALUE</div>
            )}
            <div className={styles.planName}>{selectedPlan.name}</div>
            <div className={styles.priceSection}>
              <div className={styles.totalPrice}>
                ₹{selectedPlan.price}
                <span className={styles.totalApps}>
                  Total for {selectedPlan.credits} Credits
                </span>
              </div>
            </div>
            <ul className={styles.featuresList}>
              {selectedPlan.features.map((feature, i) => (
                <li key={i}>
                  <i></i>
                  <span>{renderFeature(feature)}</span>
                </li>
              ))}
            </ul>
            <button
              className={styles.payButton}
              onClick={handleStartPayment}
              disabled={isLoading || !userEmail}
              title={
                !userEmail
                  ? "Login required"
                  : `Proceed to pay for ${selectedPlan.name}`
              }
            >
              {isLoading ? "Processing..." : `Pay ₹${selectedPlan.price} Now`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Buy;
